// v3.18-1328 — Send Combined Offers to Agent.
//
// Delivery mechanism is unchanged from v3.18-1326 (POST /api/email/send
// with activity_type = "agent_offer_sent") — no new communication
// workflow was introduced.  The outbound HTML is generated from the
// dedicated ``stackedFinancingEmail`` builder so the agent receives a
// professional multi-loan-card financing package instead of a plain-
// text summary.  The build is strictly LENDER-NEUTRAL — no lender
// names, IDs, phones, emails, portal URLs, credentials, or any other
// provider identifier ever reaches the outbound message.  Internal
// Average Interest Rate is intentionally excluded from the outbound
// (internal staff only, visible in the Hub UI).
//
// A separate immutable audit row is written server-side capturing the
// underlying financing sources (internal rates, provider IDs, operator,
// timestamp, combined totals).  That row is INTERNAL ONLY — never seen
// by the sales representative, agent, or customer.
import { useEffect, useMemo, useState } from 'react';
import useLiveActivityClaim from '../hooks/useLiveActivityClaim';   // v3.18-1343 LAC-VERIFY
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Loader2, Send, ShieldCheck, Eye, Code2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  buildStackedFinancingEmailHTML,
  buildStackedFinancingEmailSubject,
  STACKED_EMAIL_DEFAULT_INTRO,
  STACKED_EMAIL_DEFAULT_NOTES,
} from '../lib/stackedFinancingEmail';

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === '') return '-';
  const n = Number(v);
  return isNaN(n) ? String(v) : new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n);
};

export default function SendCombinedOffersToAgentModal({
  open,
  onOpenChange,
  client,
  offers: propOffers,   // v3.18-1329 — optional; the modal re-fetches from the server on open
  hasSavedStack: propHasSavedStack = true,
}) {
  const [to, setTo]       = useState('');
  const [cc, setCc]       = useState('');
  const [subject, setSubj] = useState('');
  const [intro, setIntro]  = useState(STACKED_EMAIL_DEFAULT_INTRO);
  const [notes, setNotes]  = useState(STACKED_EMAIL_DEFAULT_NOTES);
  const [showSource, setShowSource] = useState(false);
  const [bodyOverride, setBodyOverride] = useState(null);
  const [sending, setSending] = useState(false);
  // v3.18-1329 — the modal fetches its own copy of the saved stack on
  // open so a stale ``client`` prop from the parent can never leak
  // in-memory-only UI selections into the outbound package.
  const [freshStack, setFreshStack] = useState(null);
  const [loading, setLoading] = useState(false);

  // v3.18-1343 LAC-VERIFY — declare "submitting financing offers"
  // for this client so peers know the offer package is in flight.
  useLiveActivityClaim({
    clientId: client?.id,
    workflow: 'submitting_financing_offers',
    enabled:  !!(open && client?.id),
  });

  useEffect(() => {
    if (!open || !client?.id) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/clients/${client.id}/offer-stack`)
      .then(({ data }) => { if (!cancelled) setFreshStack(data?.offer_stack || null); })
      .catch(() => { if (!cancelled) setFreshStack(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, client?.id]);

  // Build ``offers`` STRICTLY from the freshly-fetched saved stack.
  // Values come from the current client doc (approved_amount, payment,
  // rate, term) but the SELECTION comes from ``freshStack.offer_ids``.
  const offers = useMemo(() => {
    const ids = Array.isArray(freshStack?.offer_ids) ? freshStack.offer_ids : [];
    if (!freshStack?.enabled || ids.length === 0) return [];
    const offer1 = {
      offerNumber: 1,
      approved_amount: client?.approved_amount,
      payment:         client?.payment || client?.monthly_payment,
      rate:            client?.rate    || client?.apr,
      term:            client?.term,
      lender_name:     client?.lender_name || null,
      lender_id:       client?.lender_id || null,
    };
    const o2raw = client?.financing_option_2 || {};
    const offer2 = {
      offerNumber: 2,
      approved_amount: o2raw.approved_amount,
      payment:         o2raw.payment,
      rate:            o2raw.rate,
      term:            o2raw.term,
      lender_name:     o2raw.lender_name || null,
      lender_id:       o2raw.lender_id || null,
    };
    const map = { 1: offer1, 2: offer2 };
    return ids.map(id => map[id]).filter(o => o && o.approved_amount);
  }, [freshStack, client]);

  const hasSavedStack = !!freshStack?.enabled && offers.length >= 2;
  // While the initial fetch is in flight fall back to whatever the
  // parent handed us to avoid a Flash Of Empty State on open.
  const effectiveHasSaved = loading ? propHasSavedStack : hasSavedStack;
  const effectiveOffers   = loading ? (propOffers || []) : offers;

  const combined = useMemo(() => (effectiveOffers || []).reduce(
    (acc, o) => ({
      approved: acc.approved + (Number(o.approved_amount) || 0),
      payment:  acc.payment  + (Number(o.payment) || 0),
      count:    acc.count + 1,
    }),
    { approved: 0, payment: 0, count: 0 }
  ), [effectiveOffers]);

  const generatedHtml = useMemo(
    () => buildStackedFinancingEmailHTML({ client, offers: effectiveOffers, intro, notes }),
    [client, effectiveOffers, intro, notes]
  );
  const effectiveHtml = bodyOverride ?? generatedHtml;

  useEffect(() => {
    if (!open || !effectiveOffers?.length) return;
    setTo('');
    setCc('');
    setSubj(buildStackedFinancingEmailSubject(client));
    setIntro(STACKED_EMAIL_DEFAULT_INTRO);
    setNotes(STACKED_EMAIL_DEFAULT_NOTES);
    setBodyOverride(null);
    setShowSource(false);
    if (client?.id) {
      api.get(`/clients/${client.id}/default-recipients`, { params: { context: 'external' } })
        .then(({ data }) => {
          const team = (data?.cc || []).filter(Boolean);
          if (team.length) setCc(team.join(', '));
        })
        .catch(() => { /* leave blank */ });
    }
  }, [open, client?.id, effectiveOffers?.length, client]);

  const send = async () => {
    if (!effectiveHasSaved || !effectiveOffers || effectiveOffers.length < 2) {
      toast.error('No saved financing stack exists.');
      return;
    }
    if (!to.trim()) { toast.error('"To" address is required'); return; }
    if (!subject.trim() || !effectiveHtml.trim()) {
      toast.error('Subject and body cannot be empty'); return;
    }
    setSending(true);
    try {
      // Internal-only audit payload: every underlying financing source,
      // internal rates, and provider IDs. This flows into activity_metadata
      // and the backend writes an immutable system_audit row.
      const audit = {
        offer_ids:     effectiveOffers.map(o => o.offerNumber),
        combined_approved_amount: Math.round(combined.approved * 100) / 100,
        combined_monthly_payment: Math.round(combined.payment * 100) / 100,
        offers_included: combined.count,
        internal_offers: effectiveOffers.map(o => ({
          offer_number: o.offerNumber,
          approved_amount: Number(o.approved_amount) || 0,
          monthly_payment: Number(o.payment) || 0,
          interest_rate:   Number(o.rate) || 0,
          loan_term:       Number(o.term) || 0,
          lender_name:     o.lender_name || null,
          lender_id:       o.lender_id || null,
        })),
      };
      const payload = {
        client_id: client?.id,
        to_email: to.trim(),
        cc: cc.trim() || null,
        subject: subject.trim(),
        message: effectiveHtml,
        template: 'custom',
        activity_type: 'agent_offer_sent',
        activity_metadata: {
          source: 'send_combined_offers_to_agent',
          combined_offers: audit,
          recipient: to.trim(),
          system_version: 'v3.18-1328',
        },
      };
      await api.post('/email/send', payload);
      toast.success(`Combined offer (${combined.count} offers) sent to ${to.trim()}`);
      onOpenChange(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to send combined offers';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-y-auto"
        data-testid="send-combined-offers-modal"
      >
        <DialogHeader>
          <DialogTitle>Send Stacked Financing Package</DialogTitle>
        </DialogHeader>

        {(!effectiveHasSaved || !effectiveOffers || effectiveOffers.length < 2) ? (
          <div className="space-y-3">
            <div
              className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900"
              data-testid="send-combined-offers-empty"
            >
              <ShieldCheck className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                <div className="font-semibold">No saved financing stack exists.</div>
                <div className="text-xs">
                  Enable Stack Offers, select the financing offers you want to combine,
                  and the stack will save automatically. Once at least two offers are
                  saved the Send Stacked Package action becomes available.
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="send-combined-offers-cancel"
              >Close</Button>
            </DialogFooter>
          </div>
        ) : (
        <>
        <div className="space-y-3">
          {/* Neutrality reassurance — visible to the internal operator only */}
          <div
            className="flex items-start gap-2 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800"
            data-testid="send-combined-neutrality-notice"
          >
            <ShieldCheck className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>
              The outbound message contains ONLY per-loan and combined totals.
              No lender names, IDs, or provider-specific information will be
              exposed to the agent. Internal Average Interest Rate is omitted
              from the outbound (visible only inside the Hub).
            </span>
          </div>

          {/* Combined snapshot preview */}
          <div className="grid grid-cols-3 gap-2 rounded border border-slate-200 bg-slate-50/60 px-3 py-2 text-xs">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Combined Approved</div>
              <div className="font-semibold text-slate-800" data-testid="send-combined-summary-approved">
                {fmtMoney(combined.approved)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Combined Payment</div>
              <div className="font-semibold text-slate-800" data-testid="send-combined-summary-payment">
                {fmtMoney(combined.payment)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Loans Included</div>
              <div className="font-semibold text-slate-800" data-testid="send-combined-summary-count">
                {combined.count}
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">
              To <span className="text-red-500">*</span>
            </label>
            <Input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="agent@example.com"
              type="email"
              data-testid="send-combined-offers-to"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">
              CC <span className="text-slate-400 font-normal">(comma-separated)</span>
            </label>
            <Input
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="manager@yourcompany.com"
              data-testid="send-combined-offers-cc"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Subject</label>
            <Input
              value={subject}
              onChange={(e) => setSubj(e.target.value)}
              data-testid="send-combined-offers-subject"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Intro</label>
              <Textarea
                value={intro}
                onChange={(e) => { setIntro(e.target.value); setBodyOverride(null); }}
                rows={4}
                className="text-xs"
                data-testid="send-combined-offers-intro"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Closing Notes</label>
              <Textarea
                value={notes}
                onChange={(e) => { setNotes(e.target.value); setBodyOverride(null); }}
                rows={4}
                className="text-xs"
                data-testid="send-combined-offers-notes"
              />
            </div>
          </div>

          {/* Live preview / raw source toggle */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-slate-600">Message Preview</label>
              <button
                type="button"
                onClick={() => setShowSource(v => !v)}
                className="text-[11px] text-slate-500 hover:text-slate-800 underline inline-flex items-center gap-1"
                data-testid="send-combined-offers-toggle-source"
              >
                {showSource
                  ? <><Eye className="h-3 w-3" /> Show rendered preview</>
                  : <><Code2 className="h-3 w-3" /> Edit raw HTML</>}
              </button>
            </div>
            {showSource ? (
              <Textarea
                value={effectiveHtml}
                onChange={(e) => setBodyOverride(e.target.value)}
                rows={12}
                className="font-mono text-[11px] leading-snug"
                data-testid="send-combined-offers-body"
              />
            ) : (
              <div
                className="rounded border border-slate-200 bg-white overflow-hidden"
                data-testid="send-combined-offers-preview"
              >
                <iframe
                  title="Stacked Financing Package preview"
                  srcDoc={effectiveHtml}
                  sandbox=""
                  style={{ width: '100%', height: '420px', border: 0, background: '#f1f5f9' }}
                />
              </div>
            )}
            {bodyOverride !== null && (
              <div className="mt-1 text-[10px] text-amber-700">
                Body has been manually edited. Editing Intro or Notes will regenerate the message.
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={sending}
            data-testid="send-combined-offers-cancel"
          >Cancel</Button>
          <Button
            type="button"
            onClick={send}
            disabled={sending || !effectiveHasSaved || combined.count < 2}
            data-testid="send-combined-offers-send"
          >
            {sending
              ? <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Sending…</>
              : <><Send className="h-3 w-3 mr-1" /> Send Stacked Package</>}
          </Button>
        </DialogFooter>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}
